/**
 * Props:
 *  - lesson {object}: the lesson data to render (see shape below)
 *
 * Lesson shape:
 * {
 *   chapter:    string,
 *   title:      string,
 *   duration:   string,   e.g. "8 min read"
 *   tags:       string[],
 *   intro:      string,
 *   sections: [
 *     {
 *       heading: string,
 *       body:    string,         (supports \n for paragraphs)
 *       code?:   { lang: string, snippet: string },
 *       tip?:    string,
 *       warning? string,
 *     }
 *   ],
 *   quiz?: {
 *     question: string,
 *     options:  string[],
 *     answer:   number,   (0-based index)
 *   }
 * }
 */

export const DEMO_LESSON = {
    chapter: 'Chapter 1: Foundations',
    title: 'Setting up your environment',
    duration: '8 min read',
    tags: ['Setup', 'Node.js', 'VS Code'],
    intro:
        'Before you write a single line of code you need a solid workspace. In this lesson we\'ll install the tools that every professional developer relies on daily: and make sure they\'re configured correctly from the start.',
    sections: [
        {
            heading: 'Installing Node.js',
            body: 'Node.js is the runtime that lets you execute JavaScript outside a browser. Head to nodejs.org and download the LTS release: "Long Term Support" means it\'s the most stable version available.\n\nOnce installed, verify it worked by opening your terminal and running the commands below. You should see version numbers printed back at you.',
            code: {
                lang: 'bash',
                snippet: `node --version\n# v20.11.0\n\nnpm --version\n# 10.2.4`,
            },
        },
        {
            heading: 'Configuring VS Code',
            body: 'Visual Studio Code is the editor of choice for most React developers. Download it from code.visualstudio.com, then install the extensions below to unlock syntax highlighting, auto-formatting, and intelligent code completion.',
            tip: 'Press Ctrl + Shift + X (or Cmd + Shift + X on Mac) to open the Extensions panel directly.',
            code: {
                lang: 'text',
                snippet: `ESLint                 , dbaeumer.vscode-eslint\nPrettier               , esbenp.prettier-vscode\nES7+ React Snippets    , dsznajder.es7-react-js-snippets\nCSS Modules            , clinyong.vscode-css-modules`,
            },
        },
        {
            heading: 'Creating your first project',
            body: 'With Node.js installed, the npm package manager is available globally. Use the Vite scaffolding tool to spin up a production-ready React project in seconds.',
            code: {
                lang: 'bash',
                snippet: `npm create vite@latest my-app -- --template react\ncd my-app\nnpm install\nnpm run dev`,
            },
            warning:
                'Make sure you are not inside a OneDrive or Dropbox folder when creating projects: file-sync tools can interfere with the Node modules installation.',
        },
        {
            heading: 'Understanding the file structure',
            body: 'Once Vite finishes scaffolding, you\'ll see a handful of files. The ones you\'ll touch most often are:\n\n• src/main.jsx, the entry point that mounts your React app into the HTML\n• src/App.jsx, the root component, your starting canvas\n• index.html, the single HTML shell that React hydrates\n• vite.config.js, build and dev-server configuration',
        },
    ],
    quiz: {
        question: 'Which npm command starts the Vite development server?',
        options: ['npm start', 'npm run dev', 'npm serve', 'npm run build'],
        answer: 1,
    },
};

export const DEMO_COMPLETE_RESPONSE = {
    completed: true,
    courseProgress: 36, // percentage, recalculated after completion
};
